import React, { useCallback, useEffect, useState } from 'react';

import UFilterSelect from 'Components/unit/UFilterSelect/UFilterSelect';
import { fetchAudienceGroupsFn } from 'Features/audience/stores/thunks/audience-group.thunks';
import type { Filter, FilterItem } from 'Libs/filter/types';

import styles from './AudienceSelect.style';

type AudienceSelectProps = {
  selectedAudienceGroupsIds: number[];
  handleSelectedItems: (filterCategory: 'audienceGroups', value: FilterItem[]) => void;
};

const AudienceSelect: React.FC<AudienceSelectProps> = ({ selectedAudienceGroupsIds, handleSelectedItems }) => {
  const [audienceGroups, setAudienceGroups] = useState<Filter>({
    category: 'audienceGroup',
    type: 'multiselect',
    items: [],
  });

  const fetchAudienceGroups = useCallback(async () => {
    const audienceGroups = await fetchAudienceGroupsFn({
      limit: 10000,
      offset: 0,
      title: null,
      status: 'all',
      includeMetaGroupAll: true,
      ruleType: 'segmentation',
    });

    const items: FilterItem[] = audienceGroups.items
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((audienceGroup) => {
        return {
          category: 'audienceGroup',
          id: audienceGroup.id,
          value: audienceGroup.name,
          selected: selectedAudienceGroupsIds.includes(audienceGroup.id),
        } as const;
      });

    setAudienceGroups({ category: 'audienceGroup', type: 'multiselect', items });
  }, [selectedAudienceGroupsIds]);

  const onFilterChange = useCallback(
    (value: Filter) => {
      setAudienceGroups(value);

      const selectedAudienceGroups = value.items.filter((item) => item.selected);
      handleSelectedItems('audienceGroups', selectedAudienceGroups);
    },
    [handleSelectedItems],
  );

  // Retrieve audience groups list on mount
  useEffect(() => {
    fetchAudienceGroups();
  }, []);

  // Update selected audience groups on selectedAudienceGroupsIds change
  useEffect(() => {
    if (audienceGroups.items.length > 0) {
      const newAudienceGroupsItems = audienceGroups.items.map((item) => {
        return {
          ...item,
          selected: selectedAudienceGroupsIds.includes(item.id),
        };
      });

      setAudienceGroups({ category: 'audienceGroup', type: 'multiselect', items: newAudienceGroupsItems });
    }
  }, [selectedAudienceGroupsIds]);

  return <UFilterSelect filter={audienceGroups} onFilterChanged={onFilterChange} style={styles.filter} />;
};

export default AudienceSelect;
